import React from "react";
import { UserSkillItem } from "../../../types/models/UserSkillItem";
import { Box, Typography } from "@mui/material";
import { fontColor } from "../../../constants/ColorSets";
import CheckIcon from "@mui/icons-material/Check";
import QuestionMark from "@mui/icons-material/QuestionMark";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { theme } from "../../../themes/theme";
import EmptyResult from "../../rules/EmptyResult";
import AppScrollbar from "../../../components/AppBase/AppScrollbar";
import { formatExpireDate } from "../../../utils/StringUtils";

interface UserTrainingListProp {
  trainingList: UserSkillItem[];
}

export const UserTrainingList: React.FC<UserTrainingListProp> = ({ trainingList }) => {
  const contentHeight = "calc(66vh - 210px)";

  const getStatusIcon = (status: string | undefined) => {
    switch (status) {
      case "Expired":
        return (
          <WarningAmberRoundedIcon
            sx={{ color: theme.component.alertOverdueColor, fontSize: "20px" }}
          />
        );
      case "Unknown":
        return <QuestionMark sx={{ color: "lightgray", fontSize: "20px" }} />;

      default:
        return <CheckIcon sx={{ color: theme.component.alertResolvedColor, fontSize: "20px" }} />;
    }
  };

  const getExpireDateElement = (dateValue: string, expired: boolean) => {
    const formattedDate = formatExpireDate(dateValue);

    if (formattedDate) {
      return (
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          {expired ? (
            <Typography sx={{ fontSize: 12, color: fontColor.redFontColor }}>Expired</Typography>
          ) : (
            <Typography sx={{ fontSize: 12, color: fontColor.subTitleFontColor }}>
              Expires on
            </Typography>
          )}
          <Typography sx={{ fontSize: 13 }}>{formattedDate}</Typography>
        </Box>
      );
    } else {
      return (
        <Typography sx={{ fontSize: 13, color: fontColor.subTitleFontColor, textAlign: "center" }}>
          N/A
        </Typography>
      );
    }
  };

  if (trainingList && trainingList.length > 0) {
    return (
      <AppScrollbar
        autoHide={false}
        sx={{
          maxHeight: contentHeight,
          pt: 0.5,
        }}
      >
        {trainingList.map((training) => (
          <Box
            key={training.SkillId}
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "space-between",
              px: 2,
              minHeight: "50px",
              borderTop: "1px solid lightgray",
              my: 0,
            }}
          >
            <Box
              sx={{
                display: "flex",
                width: "100%",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                {getStatusIcon(training.ExpiryStatus)}
                <Box
                  sx={{
                    ml: "4px",
                    fontSize: 14,
                    fontVariantNumeric: "tabular-nums",
                    fontFeatureSettings: "tnum",
                    textAlign: "right",
                  }}
                >
                  {training.SkillId}
                </Box>
                <Box sx={{ mx: "4px" }}>-</Box>
                <Box sx={{ fontSize: 14 }}>{training.SkillName}</Box>
              </Box>
              <Box sx={{ minWidth: "94px" }}>
                <Box sx={{ display: "flex", flexDirection: "column", ml: 1 }}>
                  {getExpireDateElement(
                    training.ExpirationDate,
                    training.ExpiryStatus == "Expired"
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        ))}
      </AppScrollbar>
    );
  } else {
    return (
      <Box sx={{ py: 4, borderTop: "1px solid lightgray" }}>
        <EmptyResult>There is currently no training data available</EmptyResult>
      </Box>
    );
  }
};
